import React from 'react';
import { Box, VStack, Button, Text, Card, HStack } from '@chakra-ui/react';
import { BottomSheet } from '../../../components/ui/custom/bottom-sheet';
import { QRScanContent } from './QRScan/QRScanContent';
import { MyQRContent } from './MyQR/MyQRContent';
import { AmountInputContent } from './QRScan/AmountInputContent';
import { PaymentConfirmContent } from './QRScan/PaymentConfirmContent';
import { PaymentSuccessContent } from './QRScan/PaymentSuccessContent';
import { PaymentErrorContent } from './QRScan/PaymentErrorContent';
import { QRScanIcon, QRDisplayIcon } from '../../../components/payments/payment-icons';
import { usePaymentDrawers } from '../../../hooks/payments/usePaymentDrawers';

const Payments = () => {
  const {
    isScanDrawerOpen,
    isMyQRDrawerOpen,
    isPaymentDrawerOpen,
    currentStep,
    scannedData,
    paymentAmount,
    transactionId,
    errorMessage,
    canGoBack,
    handleScanClick,
    handleMyQRClick,
    handleScanClose,
    handleQRScan,
    handleResetScanned,
    handleAmountSubmit,
    handlePaymentConfirm,
    handlePaymentCancel,
    handlePaymentDrawerClose,
    handleRetryPayment,
    handleGoBack,
    setIsScanDrawerOpen,
    setIsMyQRDrawerOpen,
  } = usePaymentDrawers();

  return (
    <Box w="full" h="100%">
      <VStack gap={6} w="full" maxW="450px" mx="auto" mb="80px">
        {/* 使い方ガイド */}
        <Card.Root w="full" variant="elevated" bg="white/80" borderRadius="2xl" userSelect="none">
          <Card.Body p={5}>
            <VStack gap={4} align="stretch">
              <HStack gap={3}>
                <Box
                  bg="teal.100"
                  borderRadius="full"
                  w="32px"
                  h="32px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text fontWeight="bold" color="teal.700" fontSize="sm">
                    1
                  </Text>
                </Box>
                <Box flex={1}>
                  <Text fontWeight="semibold" color="gray.800" fontSize="sm" mb={1}>
                    スキャンして支払い
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    店舗のQRコードをスキャンして金額を入力
                  </Text>
                </Box>
              </HStack>

              <HStack gap={3}>
                <Box
                  bg="purple.100"
                  borderRadius="full"
                  w="32px"
                  h="32px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text fontWeight="bold" color="purple.700" fontSize="sm">
                    2
                  </Text>
                </Box>
                <Box flex={1}>
                  <Text fontWeight="semibold" color="gray.800" fontSize="sm" mb={1}>
                    QRコードを表示
                  </Text>
                  <Text fontSize="xs" color="gray.600">
                    自分のQRコードを店舗スタッフに読み取ってもらう
                  </Text>
                </Box>
              </HStack>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* アクションボタン */}
        <VStack gap={3} w="full">
          <Button
            size="sm"
            w="full"
            h="48px"
            borderRadius="xl"
            colorPalette="teal"
            onClick={handleScanClick}
            _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
            transition="all 0.2s"
            fontSize="sm"
            fontWeight="semibold"
          >
            <QRScanIcon mr={2} />
            スキャンして支払い
          </Button>
          <Button
            size="sm"
            w="full"
            h="48px"
            borderRadius="xl"
            colorPalette="purple"
            variant="outline"
            onClick={handleMyQRClick}
            _hover={{ transform: 'translateY(-2px)', shadow: 'md', bg: 'purple.50' }}
            transition="all 0.2s"
            fontSize="sm"
            fontWeight="semibold"
          >
            <QRDisplayIcon mr={2} />
            QRコードを表示
          </Button>
        </VStack>

        {/* ヒント */}
        <Box w="full" bg="teal.50" borderRadius="lg" p={4} mt={2} userSelect="none">
          <HStack gap={2}>
            <Text fontSize="lg">💡</Text>
            <Text fontSize="xs" color="gray.700">
              <Text as="span" fontWeight="semibold">
                ヒント:
              </Text>{' '}
              どちらの方法でも残高から即座に支払いが完了します
            </Text>
          </HStack>
        </Box>
      </VStack>

      {/* QRスキャン用のボトムシート */}
      <BottomSheet
        isOpen={isScanDrawerOpen}
        onOpenChange={setIsScanDrawerOpen}
        title="QRコードをスキャン"
        description="店舗のQRコードをカメラでスキャンして支払いを開始します"
      >
        <QRScanContent
          onScan={handleQRScan}
          onClose={handleScanClose}
          isOpen={isScanDrawerOpen}
          onResetScanned={handleResetScanned}
        />
      </BottomSheet>

      {/* 支払いフロー用のボトムシート（金額入力 → 確認 → 完了 / エラー） */}
      <BottomSheet
        isOpen={isPaymentDrawerOpen}
        onOpenChange={open => {
          if (!open) handlePaymentCancel();
        }}
        title={
          currentStep === 'amount-input'
            ? '支払金額を入力'
            : currentStep === 'confirm'
            ? '支払い内容の確認'
            : currentStep === 'success'
            ? '支払い完了'
            : '支払いエラー'
        }
        description={
          currentStep === 'amount-input'
            ? '支払う金額を入力してください'
            : currentStep === 'confirm'
            ? '内容を確認して支払いを確定してください'
            : currentStep === 'success'
            ? 'お支払いが正常に完了しました'
            : '支払い処理中にエラーが発生しました'
        }
      >
        {currentStep === 'amount-input' && scannedData && (
          <AmountInputContent
            qrData={scannedData}
            onNext={handleAmountSubmit}
            onCancel={handlePaymentCancel}
            onBack={canGoBack ? handleGoBack : undefined}
          />
        )}
        {currentStep === 'confirm' && scannedData && paymentAmount !== null && (
          <PaymentConfirmContent
            qrData={scannedData}
            amount={paymentAmount}
            onConfirm={handlePaymentConfirm}
            onCancel={handlePaymentCancel}
            onBack={handleGoBack}
            canGoBack={canGoBack}
          />
        )}
        {currentStep === 'success' && scannedData && paymentAmount !== null && (
          <PaymentSuccessContent
            qrData={scannedData}
            amount={paymentAmount}
            transactionId={transactionId}
            onClose={handlePaymentDrawerClose}
          />
        )}
        {currentStep === 'error' && scannedData && paymentAmount !== null && (
          <PaymentErrorContent
            qrData={scannedData}
            amount={paymentAmount}
            errorMessage={errorMessage || '不明なエラーが発生しました'}
            onRetry={handleRetryPayment}
            onCancel={handlePaymentCancel}
          />
        )}
      </BottomSheet>

      {/* QRコード表示用のボトムシート */}
      <BottomSheet
        isOpen={isMyQRDrawerOpen}
        onOpenChange={setIsMyQRDrawerOpen}
        title="マイQRコード"
        description="このQRコードを店舗スタッフに読み取ってもらうことで支払いができます"
      >
        <MyQRContent />
      </BottomSheet>
    </Box>
  );
};

export default Payments;
